import { useCallback, useEffect, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import { useReselector } from '@/utils/sharedHooks'
import {
  BankingDisplayState,
  selectBankingDisplayState,
} from '../banking.selectors'
import {
  isPlaidConnectReminderSuppressed,
  setPlaidConnectReminderSuppressed,
} from './suppression'

const EXCLUDED_PATH_PATTERNS = ['/onboarding-v2/*', '/accounts/connect/*']
const BANKING_PATH_PATTERN = '/banking/*'

export interface PlaidConnectReminderTrigger {
  shouldShow: boolean
  dismiss: () => void
}

export const usePlaidConnectReminderTrigger =
  (): PlaidConnectReminderTrigger => {
    const displayState = useReselector(selectBankingDisplayState)
    const { pathname } = useLocation()
    const [sessionDismissed, setSessionDismissed] = useState(false)
    const [bankingVisitDismissed, setBankingVisitDismissed] = useState(false)

    const qualifies = displayState === BankingDisplayState.ApprovedNoAccounts
    const inExcludedRoute = EXCLUDED_PATH_PATTERNS.some(
      (p) => matchPath(p, pathname) !== null
    )
    const isOnBanking = matchPath(BANKING_PATH_PATTERN, pathname) !== null

    // Reset per-visit dismissal whenever the user navigates away from
    // /banking — re-entering /banking should re-open the modal until Plaid
    // is connected.
    useEffect(() => {
      if (!isOnBanking) {
        setBankingVisitDismissed(false)
      }
    }, [isOnBanking])

    const suppressed = isOnBanking
      ? bankingVisitDismissed
      : sessionDismissed || isPlaidConnectReminderSuppressed()

    const shouldShow = qualifies && !inExcludedRoute && !suppressed

    const dismiss = useCallback(() => {
      setPlaidConnectReminderSuppressed()
      setSessionDismissed(true)
      setBankingVisitDismissed(true)
    }, [])

    return { shouldShow, dismiss }
  }
